import { Button, Card, Form, Input, Select, Space, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Row = {
  id: string;
  run_no: string;
  title: string;
  status: string;
  student_count: number;
  error_count: number;
  warning_count: number;
  session_name?: string;
  session_code?: string;
  campus_name?: string;
};

export default function UdiseRunListPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.udise.manage') || user?.permissions?.includes('education.admin')
  );
  const [status, setStatus] = useState<string | undefined>();
  const [q, setQ] = useState('');

  const list = useQuery({
    queryKey: ['education-udise-runs', status, q],
    queryFn: async () => {
      const res = await api.get<Row[]>('/education/udise/runs', {
        params: { status, q: q || undefined },
      });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  return (
    <EducationPageChrome
      title={t('udiseRuns')}
      description={t('udiseRunsIntro')}
      extra={
        <Space>
          <Button onClick={() => navigate('/education/udise')}>{t('udise')}</Button>
          {canManage ? (
            <Button type="primary" onClick={() => navigate('/education/udise/runs/new')}>
              {t('newUdiseRun')}
            </Button>
          ) : null}
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Form layout="inline" onFinish={() => list.refetch()}>
            <Form.Item label={t('status')}>
              <Select
                allowClear
                style={{ minWidth: 140 }}
                value={status}
                onChange={(v) => setStatus(v || undefined)}
                options={[
                  { value: 'draft', label: t('statusDraft') },
                  { value: 'validated', label: t('statusValidated') },
                  { value: 'exported', label: t('statusExported') },
                  { value: 'cancelled', label: t('statusCancelled') },
                ]}
              />
            </Form.Item>
            <Form.Item label={t('search')}>
              <Input value={q} onChange={(e) => setQ(e.target.value)} allowClear style={{ width: 180 }} />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={list.isFetching}>
              {t('filter')}
            </Button>
          </Form>
        </Card>
        <Card size="small">
          <Table
            rowKey="id"
            size="small"
            loading={list.isLoading}
            dataSource={list.data ?? []}
            pagination={{ pageSize: 25 }}
            onRow={(row) => ({
              onClick: () => navigate(`/education/udise/runs/${row.id}`),
              style: { cursor: 'pointer' },
            })}
            columns={[
              { title: t('runNo'), dataIndex: 'run_no', width: 160 },
              { title: t('title'), dataIndex: 'title' },
              {
                title: t('session'),
                render: (_: unknown, row: Row) => row.session_name || row.session_code || '—',
              },
              { title: t('students'), dataIndex: 'student_count', width: 90 },
              { title: t('errors'), dataIndex: 'error_count', width: 80 },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 110,
                render: (v: string) => <Tag>{v}</Tag>,
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
